import Swal from "sweetalert2";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const getSalaryUserProfile = (salary, users = []) => {
  const salaryUser = salary?.user || {};
  const masterUser = users.find((user) => String(user.id) === String(salary?.user_id || salaryUser.id)) || {};
  return {
    ...masterUser,
    ...salaryUser,
    department: salaryUser.department || masterUser.department,
    position: salaryUser.position || masterUser.position,
  };
};

const getSalaryItemAmount = (item = {}) => {
  const amount = Number(item.amount ?? item.price ?? 0);
  const quantity = Number(item.quantity || 1);
  return amount * quantity;
};

const getItemLabel = (item = {}) => {
  const name = item.name || item.facility?.name || item.insurance?.name || item.deduction?.name || "-";
  const quantity = Number(item.quantity || 1);
  const amount = getSalaryItemAmount(item);
  return quantity > 1 ? `${name} x${quantity} (${amount})` : `${name} (${amount})`;
};

const escapeHtml = (value) =>
  String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");

const getFormattedItemLabel = (item = {}, formatCurrency) => {
  const name = item.name || item.facility?.name || item.insurance?.name || item.deduction?.name || "-";
  const quantity = Number(item.quantity || 1);
  const amount = Number(item.amount ?? item.price ?? 0);
  const total = getSalaryItemAmount(item);
  return quantity > 1
    ? `${name}: ${quantity} x ${formatCurrency(amount)} = ${formatCurrency(total)}`
    : `${name}: ${formatCurrency(total)}`;
};

const joinFormattedItems = (items = [], formatCurrency) => (
  items.length > 0 ? items.map((item) => escapeHtml(getFormattedItemLabel(item, formatCurrency))).join("<br>") : "-"
);

const getApiMorningMealTotal = (salary = {}) => {
  const fields = [
    "morning_meal_total",
    "morningMealTotal",
    "total_morning_meal_cost",
    "totalMorningMealCost",
    "total_morning_meal",
    "totalMorningMeal",
    "morning_meal_cost",
    "morningMealCost",
  ];
  const key = fields.find((field) => Object.prototype.hasOwnProperty.call(salary, field));
  if (!key) return null;
  const value = Number(salary[key] || 0);
  return Number.isFinite(value) ? value : 0;
};

const isMorningMealItem = (item = {}) => /makan pagi|uang makan pagi/i.test(String(item.name || item.facility?.name || ""));

const getMorningMealItem = (salary = {}) => (salary.facilities || []).find(isMorningMealItem);

const getMorningMealTotal = (salary = {}) => {
  const apiTotal = getApiMorningMealTotal(salary);
  if (apiTotal !== null) return apiTotal;
  const item = getMorningMealItem(salary);
  return item ? getSalaryItemAmount(item) : 0;
};

const getMorningMealDays = (salary = {}) => Number(
  salary.morning_meal_days ??
  salary.morningMealDays ??
  getMorningMealItem(salary)?.quantity ??
  0
);

const getMorningMealRate = (salary = {}) => Number(
  salary.morning_meal_rate ??
  salary.morningMealRate ??
  getMorningMealItem(salary)?.amount ??
  0
);

const getOtherFacilities = (salary = {}) => (salary.facilities || []).filter((item) => !isMorningMealItem(item));

const getOtherFacilityTotal = (salary = {}) => {
  if ((salary.facilities || []).length > 0) {
    return getOtherFacilities(salary).reduce((total, item) => total + getSalaryItemAmount(item), 0);
  }
  const explicitValue = salary.other_facility_total ?? salary.total_other_facilities ?? salary.facility_total_excluding_meal;
  if (explicitValue !== undefined && explicitValue !== null) return Number(explicitValue || 0);
  return Number(salary.total_facilities || 0);
};

const formatDate = (value) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("id-ID", { year: "numeric", month: "long" });
};

const formatPayrollPeriod = (period = {}) => {
  if (!period.startDate || !period.endDate) return "";
  const formatter = new Intl.DateTimeFormat("id-ID", { day: "2-digit", month: "short", year: "numeric" });
  return `${formatter.format(new Date(period.startDate))} - ${formatter.format(new Date(period.endDate))}`;
};

const SalarySummaryReport = ({ fetchAllSalaries, filterOptions, payrollPeriod, formatCurrency, users = [], isDisabled }) => {
  const handleExport = async () => {
    Swal.fire({
      title: "Memproses PDF...",
      text: "Mohon tunggu sejenak, sedang mengambil seluruh data sesuai filter",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetchAllSalaries();
      if (!response.success || !response.data) {
        throw new Error("Gagal mengambil data");
      }

      const allData = response.data;
      if (allData.length === 0) {
        Swal.fire("Info", "Tidak ada data untuk diekspor", "info");
        return;
      }

      const monthName = filterOptions.filterMonth
        ? new Date(new Date().getFullYear(), filterOptions.filterMonth - 1, 1).toLocaleString("id-ID", { month: "long" })
        : "Semua Bulan";
      const yearName = filterOptions.filterYear || new Date().getFullYear();
      const periodLabel = formatPayrollPeriod(payrollPeriod) || `${monthName} ${yearName}`;

    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <html>
        <head>
          <title>Rekap Gaji - ${periodLabel}</title>
          <style>
            @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;700&display=swap');
            body { font-family: 'Inter', sans-serif; margin: 0; padding: 20px; color: #333; }
            .watermark {
              position: fixed;
              top: 50%;
              left: 50%;
              transform: translate(-50%, -50%);
              opacity: 0.05;
              width: 500px;
              z-index: -1;
              pointer-events: none;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #333;
              padding-bottom: 15px;
              margin-bottom: 20px;
            }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            th { background-color: #f8f9fa; border: 1px solid #dee2e6; padding: 8px; text-align: left; }
            td { padding: 7px; border: 1px solid #dee2e6; vertical-align: top; }
            .total-row { background-color: #f8f9fa; font-weight: bold; }
            .detail-cell { line-height: 1.45; min-width: 190px; }
            @page { size: A4 landscape; margin: 0.8cm; }
            @media print {
              .no-print { display: none; }
              body { padding: 0; }
            }
          </style>
        </head>
        <body>
          <div class="watermark">
            <img src="/Kuber.jpeg" style="width: 100%;" />
          </div>

          <div class="header">
            <h1 style="margin: 0; font-size: 22px; letter-spacing: 1px;">KUBER BALI ADVENTURE</h1>
            <p style="margin: 5px 0 0 0; font-weight: bold; color: #444;">REKAPITULASI PEMBAYARAN GAJI</p>
            <p style="margin: 2px 0 0 0; color: #666; font-size: 12px;">Periode: ${periodLabel}</p>
          </div>
          
          <table>
            <thead>
              <tr>
                <th style="text-align: center; width: 50px;">NO</th>
                <th>NAMA KARYAWAN</th>
                <th style="width: 100px;">DEPT</th>
                <th style="text-align: center; width: 60px;">HARI</th>
                <th style="text-align: right; width: 110px;">GAJI POKOK</th>
                <th>TAMBAHAN</th>
                <th>PENGURANGAN</th>
                <th style="width: 150px;">REKENING</th>
                <th style="text-align: right; width: 150px;">GAJI BERSIH</th>
              </tr>
            </thead>
            <tbody>
              ${allData.map((s, i) => {
                const user = getSalaryUserProfile(s, users);
                const facilities = s.facilities || [];
                const insurances = s.insurances || [];
                const workBonuses = s.work_bonuses || s.workBonuses || [];
                const others = s.others || [];
                const deductions = s.deductions || [];
                const additions = [...facilities, ...insurances, ...workBonuses, ...others];

                return `
                <tr>
                  <td style="text-align: center;">${i + 1}</td>
                  <td style="font-weight: bold;">${escapeHtml(user.name || s.user_id)}</td>
                  <td>${escapeHtml(user.department?.name || s.department_name || "-")}</td>
                  <td style="text-align: center;">${Number(s.total_work || 0)}</td>
                  <td style="text-align: right;">${formatCurrency(s.basic_salary)}</td>
                  <td class="detail-cell">${joinFormattedItems(additions, formatCurrency)}</td>
                  <td class="detail-cell">${joinFormattedItems(deductions, formatCurrency)}</td>
                  <td>${escapeHtml(user.nomor_rekening || "-")}</td>
                  <td style="text-align: right; font-weight: bold;">${formatCurrency(s.total_salary)}</td>
                </tr>
              `;
              }).join("")}
            </tbody>
          </table>
          
          <div style="margin-top: 10px; border-top: 2px solid #333; padding-top: 5px;">
            <table style="width: 100%; border-collapse: collapse;">
              <tr style="font-weight: bold; background-color: #f8f9fa;">
                <td style="text-align: right; padding: 15px; border: none;">TOTAL KESELURUHAN</td>
                <td style="text-align: right; padding: 15px; font-size: 16px; color: #198754; width: 180px; border: none;">${formatCurrency(allData.reduce((acc, curr) => acc + parseFloat(curr.total_salary), 0))}</td>
              </tr>
            </table>
          </div>
          
          <div style="margin-top: 30px; text-align: right; font-size: 10px; color: #666;">
            <p>Dicetak pada: ${new Date().toLocaleString("id-ID")}</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 500);
      Swal.close();
    } catch (error) {
      console.error("Export error:", error);
      Swal.fire("Error", "Terjadi kesalahan saat mengekspor PDF", "error");
    }
  };

  const handleExportExcel = async () => {
    Swal.fire({
      title: "Memproses Excel...",
      text: "Mohon tunggu sejenak",
      allowOutsideClick: false,
      didOpen: () => {
        Swal.showLoading();
      },
    });

    try {
      const response = await fetchAllSalaries();
      if (!response.success || !response.data) {
        throw new Error("Gagal mengambil data");
      }

      const allData = response.data;
      if (allData.length === 0) {
        Swal.fire("Info", "Tidak ada data untuk diekspor", "info");
        return;
      }

      const monthName = filterOptions.filterMonth
        ? new Date(new Date().getFullYear(), filterOptions.filterMonth - 1, 1).toLocaleString("id-ID", { month: "long" })
        : "Semua Bulan";
      const yearName = filterOptions.filterYear || new Date().getFullYear();
      const periodLabel = formatPayrollPeriod(payrollPeriod) || `${monthName} ${yearName}`;

      const excelData = allData.map((s, i) => {
        const user = getSalaryUserProfile(s, users);
        const facilities = getOtherFacilities(s);
        const insurances = s.insurances || [];
        const deductions = s.deductions || [];
        const workBonuses = s.work_bonuses || s.workBonuses || [];
        const others = s.others || [];

        return {
          "No": i + 1,
          "Nama Karyawan": user.name || s.user_id,
          "Departemen": user.department?.name || s.department_name || "-",
          "Posisi": user.position?.name || s.position_name || "-",
          "Day Off": user.day_off || "-",
          "Email": user.email || "-",
          "No Tlp": user.phone || "-",
          "No Rekening": user.nomor_rekening || "-",
          "Catatan / Pelanggaran": user.staff_note || "-",
          "Periode Gaji": periodLabel || formatDate(s.date_salary),
          "Total Hari Kerja": Number(s.total_work || 0),
          "Durasi Kerja": Number(s.work_duration || 0),
          "Gaji Pokok": Number(s.basic_salary || 0),
          "Qty Uang Makan Pagi": getMorningMealDays(s),
          "Rate Uang Makan Pagi": getMorningMealRate(s),
          "Uang Makan Pagi": getMorningMealTotal(s),
          "Fasilitas Lain": getOtherFacilityTotal(s),
          "Detail Fasilitas Lain": facilities.map(getItemLabel).join(", ") || "-",
          "Total Asuransi": Number(s.total_insurances || 0),
          "Detail Asuransi": insurances.map(getItemLabel).join(", ") || "-",
          "Bonus Kerja": Number(s.total_work_bonuses || 0),
          "Detail Bonus Kerja": workBonuses.map(getItemLabel).join(", ") || "-",
          "Bonus Lain": Number(s.total_other || 0),
          "Detail Bonus Lain": others.map(getItemLabel).join(", ") || "-",
          "Potongan": Number(s.total_deductions || 0),
          "Detail Potongan": deductions.map(getItemLabel).join(", ") || "-",
          "Gaji Bersih": Number(s.total_salary || 0),
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(excelData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, "Rekap Gaji");

      // Set column widths
      const wscols = [
        { wch: 5 },
        { wch: 28 },
        { wch: 18 },
        { wch: 18 },
        { wch: 12 },
        { wch: 28 },
        { wch: 16 },
        { wch: 20 },
        { wch: 32 },
        { wch: 16 },
        { wch: 14 },
        { wch: 14 },
        { wch: 15 },
        { wch: 18 },
        { wch: 18 },
        { wch: 16 },
        { wch: 16 },
        { wch: 36 },
        { wch: 16 },
        { wch: 36 },
        { wch: 16 },
        { wch: 36 },
        { wch: 16 },
        { wch: 36 },
        { wch: 16 },
        { wch: 36 },
        { wch: 16 },
      ];
      worksheet["!cols"] = wscols;

      const excelBuffer = XLSX.write(workbook, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8" });
      
      saveAs(data, `Rekap-Gaji-${periodLabel || `${monthName}-${yearName}`}.xlsx`);
      Swal.close();
    } catch (error) {
      console.error("Excel export error:", error);
      Swal.fire("Error", "Terjadi kesalahan saat mengekspor Excel", "error");
    }
  };

  return (
    <div className="btn-list">
      <button
        className="btn btn-outline-success"
        onClick={handleExport}
        disabled={isDisabled}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="icon icon-tabler icon-tabler-file-download"
          width="24"
          height="24"
          viewBox="0 0 24 24"
          strokeWidth="2"
          stroke="currentColor"
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
          <path d="M17 21h-10a2 2 0 0 1 -2 -2v-14a2 2 0 0 1 2 -2h7l5 5v11a2 2 0 0 1 -2 2z" />
          <line x1="12" y1="11" x2="12" y2="17" />
          <polyline points="9 14 12 17 15 14" />
        </svg>
        PDF (Visual)
      </button>
      <button
        className="btn btn-outline-primary"
        onClick={handleExportExcel}
        disabled={isDisabled}
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-table-export" width="24" height="24" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <path d="M11.5 20h-5.5a2 2 0 0 1 -2 -2v-12a2 2 0 0 1 2 -2h7l5 5v2" />
          <path d="M14 3v4a1 1 0 0 0 1 1h4" />
          <path d="M15 19l3 3l3 -3" />
          <path d="M18 22v-9" />
        </svg>
        Excel (Copy Data)
      </button>
    </div>
  );
};

export default SalarySummaryReport;
